/* eslint-disable jsx-a11y/control-has-associated-label */
import Button from '@brands/Components/Button/Button';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import MinimalDateRangePicker from '@brands/Components/MinimalDateRangePicker/MinimalDateRangePicker';
import useAuditTrail from '@brands/hooks/useAuditTrail';
import useDebounce from '@brands/hooks/useDebounce';
import { UserRoleName } from '@brands/services/identity/types/UserProfile';
import { formatDate } from '@brands/Utils/formatDate';
import { triggerAuditTrailCsvDownload } from '@brands/Utils/triggerAuditTrailCsvDownload';
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi';
import { MdFilterList } from 'react-icons/md';

import downloadCSV from './downloadCSV.svg';
import { sortData, SortKeys, SortOrder } from './sortUserData';
import styles from './styles.module.scss';

const AuditTrailList = (): JSX.Element => {
  const [filterValues, setFilterValues] = useState<{ [key: string]: string | string[] }>({});
  const [selectedRangeAuditTrailDate, setSelectedRangeAuditTrailDate] = useState<[Date, Date] | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showFilterInputs, setShowFilterInputs] = useState(false);
  const observer = useRef<IntersectionObserver>();
  const lastAuditTrailItemRef = useRef<HTMLTableRowElement>(null);

  const handleFilterChange = (key: string, value: string | string[]): void => {
    setFilterValues((prevFilters) => {
      const updatedFilters = { ...prevFilters };
      if ((typeof value === 'string' && value === '') || (Array.isArray(value) && value.length === 0)) {
        delete updatedFilters[key];
      } else {
        updatedFilters[key] = value;
      }
      return updatedFilters;
    });
  };

  const params = useMemo(() => {
    const baseParams = {
      sort_by: 'created_at' as const,
      sort_direction: 'desc' as const,
      limit: 15,
      users_only: true,
      include_case_creation: true,
    };

    return {
      ...baseParams,
      ...filterValues,
    };
  }, [filterValues]);

  const debouncedParams = useDebounce(params, 1000);

  const { auditTrailList, loadNextPage, dataLoading } = useAuditTrail(debouncedParams);

  const formatDateToLocalString = (date: Date): string =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  const handleDateRangeFilterChange = (key: string, value: [Date, Date] | null): void => {
    setFilterValues((prevFilters) => {
      const updatedFilters = { ...prevFilters };
      if (value) {
        if (value[0] === value[1] || (value[0] == null && value[1] == null)) {
          delete updatedFilters.date_from;
          delete updatedFilters.date_to;
        } else {
          updatedFilters.date_from = formatDateToLocalString(value[0]);
          updatedFilters.date_to = formatDateToLocalString(value[1]);
        }
      } else {
        delete updatedFilters.date_from;
        delete updatedFilters.date_to;
      }

      return updatedFilters;
    });

    if (key === 'audit_trail') {
      setSelectedRangeAuditTrailDate(value);
    }
  };

  const loadMore = (): void => {
    setIsLoading(true);
    loadNextPage().then(() => {
      setIsLoading(false);
    });
  };

  const handleIntersection = (entries: IntersectionObserverEntry[]): void => {
    const lastItem = entries[0];
    if (lastItem.isIntersecting && !isLoading) {
      loadMore();
    }
  };

  useEffect(() => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(handleIntersection);
    if (lastAuditTrailItemRef.current) observer.current.observe(lastAuditTrailItemRef.current);
  }, [auditTrailList]);

  const headers: { key: SortKeys; label: string; isSorted: boolean }[] = [
    { key: 'user', label: 'Name', isSorted: false },
    { key: 'action_message', label: 'Action', isSorted: false },
    { key: 'actor', label: 'Modified by', isSorted: false },
    { key: 'created_at', label: 'Modification date', isSorted: false },
  ];

  const [headersState, setHeadersState] = useState(headers);
  const [sortKey, setSortKey] = useState<SortKeys>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const sortedData = useCallback(
    () => sortData({ tableData: auditTrailList, sortKey, reverse: sortOrder === 'desc' }),
    [auditTrailList, sortKey, sortOrder],
  );

  const changeSort = (key: SortKeys): void => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    setSortKey(key);
    setHeadersState(
      headersState.map((header) => {
        if (sortKey === key) {
          return { ...header, isSorted: !header.isSorted };
        }
        return { ...header, isSorted: false };
      }),
    );
  };

  const handleDownloadClick = async (): Promise<void> => {
    setIsLoading(true);
    await triggerAuditTrailCsvDownload(debouncedParams);
    setIsLoading(false);
  };

  return (
    <div className={styles.auditTrailDiv}>
      <div className={styles.filterCheckboxs}>
        <span className={`${styles.filterSpan} ${styles.filterText} fs-unmask`}>Filter by: </span>
        <div className={styles.filterCheckboxsSpanDiv}>
          <span className={styles.filterSpan}>
            <input
              className={`form-check-input fs-unmask ${styles.checkInput} mt-0 ${styles.radioBtn}`}
              type="checkbox"
              name="Patient"
              id="Patient"
              value="Patient"
              checked={Array.isArray(filterValues.user_role) && filterValues.user_role.includes(UserRoleName.Patient)}
              onChange={(e) => {
                const { checked } = e.target;
                setFilterValues((prev) => ({
                  ...prev,
                  user_role: checked ? [UserRoleName.Patient] : [],
                }));
              }}
            />
            <label className={`fs-unmask ${styles.label}`} htmlFor="Patient">
              Patient
            </label>
          </span>

          <span className={styles.filterSpan}>
            <input
              className={`form-check-input fs-unmask ${styles.checkInput} mt-0 ${styles.radioBtn}`}
              type="checkbox"
              name="Dependent"
              id="Dependent"
              value="Dependent"
              checked={Array.isArray(filterValues.user_role) && filterValues.user_role.includes(UserRoleName.Dependent)}
              onChange={(e) => {
                const { checked } = e.target;
                setFilterValues((prev) => ({
                  ...prev,
                  user_role: checked ? [UserRoleName.Dependent] : [],
                }));
              }}
            />
            <label className={`fs-unmask ${styles.label}`} htmlFor="Dependent">
              Dependent
            </label>
          </span>

          <span className={styles.filterSpan}>
            <input
              className={`form-check-input fs-unmask ${styles.checkInput} mt-0 ${styles.radioBtn}`}
              type="checkbox"
              name="CareAdvisor"
              id="CareAdvisor"
              value="CareAdvisor"
              checked={
                Array.isArray(filterValues.user_role) && filterValues.user_role.includes(UserRoleName.CareAdvisor)
              }
              onChange={(e) => {
                const { checked } = e.target;
                setFilterValues((prev) => ({
                  ...prev,
                  user_role: checked ? [UserRoleName.CareAdvisor] : [],
                }));
              }}
            />
            <label className={`fs-unmask ${styles.label}`} htmlFor="Patient">
              Care Advisor
            </label>
          </span>

          <span className={styles.filterSpan}>
            <input
              className={`form-check-input fs-unmask ${styles.checkInput} mt-0 ${styles.radioBtn}`}
              type="checkbox"
              name="Provider"
              id="Provider"
              value="Provider"
              checked={Array.isArray(filterValues.user_role) && filterValues.user_role.includes(UserRoleName.Provider)}
              onChange={(e) => {
                const { checked } = e.target;
                setFilterValues((prev) => ({
                  ...prev,
                  user_role: checked ? [UserRoleName.Provider] : [],
                }));
              }}
            />
            <label className={`fs-unmask ${styles.label}`} htmlFor="Provider">
              Provider
            </label>
          </span>

          <span className={styles.filterSpan}>
            <input
              className={`form-check-input fs-unmask ${styles.checkInput} mt-0 ${styles.radioBtn}`}
              type="checkbox"
              name="Admin"
              id="Admin"
              value="Admin"
              checked={Array.isArray(filterValues.user_role) && filterValues.user_role.includes(UserRoleName.Admin)}
              onChange={(e) => {
                const { checked } = e.target;
                setFilterValues((prev) => ({
                  ...prev,
                  user_role: checked ? [UserRoleName.Admin] : [],
                }));
              }}
            />
            <label className={`fs-unmask ${styles.label}`} htmlFor="Admin">
              Admin
            </label>
          </span>

          <span className={styles.filterSpan}>
            <input
              className={`form-check-input fs-unmask ${styles.checkInput} mt-0 ${styles.radioBtn}`}
              type="checkbox"
              name="OrganizationAdmin"
              id="OrganizationAdmin"
              value="OrganizationAdmin"
              checked={
                Array.isArray(filterValues.user_role) && filterValues.user_role.includes(UserRoleName.OrganizationAdmin)
              }
              onChange={(e) => {
                const { checked } = e.target;
                setFilterValues((prev) => ({
                  ...prev,
                  user_role: checked ? [UserRoleName.OrganizationAdmin] : [],
                }));
              }}
            />
            <label className={`fs-unmask ${styles.label}`} htmlFor="OrganizationAdmin">
              Org Admin
            </label>
          </span>

          <span className={styles.filterSpan}>
            <input
              className={`form-check-input fs-unmask ${styles.checkInput} mt-0 ${styles.radioBtn}`}
              type="checkbox"
              name="SuperAdmin"
              id="SuperAdmin"
              value="SuperAdmin"
              checked={
                Array.isArray(filterValues.user_role) && filterValues.user_role.includes(UserRoleName.SuperAdmin)
              }
              onChange={(e) => {
                const { checked } = e.target;
                setFilterValues((prev) => ({
                  ...prev,
                  user_role: checked ? [UserRoleName.SuperAdmin] : [],
                }));
              }}
            />
            <label className={`fs-unmask ${styles.label}`} htmlFor="SuperAdmin">
              Super Admin
            </label>
          </span>
        </div>
        <div className={styles.downloadCSVButton}>
          <Button onClick={handleDownloadClick} disabled={isLoading}>
            <img src={downloadCSV} alt="download" />
            Download CSV
          </Button>
        </div>
      </div>

      <div className={styles.ccDashboardBody}>
        {!dataLoading ? (
          <>
            <MdFilterList className={styles.filterIcon} onClick={() => setShowFilterInputs(!showFilterInputs)} />
            <div className={styles.tableContainer}>
              <table className={styles.allCasesTable}>
                <thead className={`${styles.providerListTableHeader} ${styles.dashboardHeaderName} ${styles.titles}`}>
                  <tr>
                    {headersState.map((row) => {
                      return (
                        <th key={row.key} onClick={() => changeSort(row.key)}>
                          <span className={`fs-unmask ${sortKey === row.key ? styles.bold : ''}`}>
                            {row.label}
                            {row.label !== '' ? sortOrder === 'asc' ? <HiChevronDown /> : <HiChevronUp /> : ''}
                          </span>
                        </th>
                      );
                    })}
                  </tr>

                  {showFilterInputs && (
                    <tr className={styles.filterInputs}>
                      <th style={{ zIndex: '99' }}>
                        <input
                          type="text"
                          className={styles.inputSearch2}
                          value={filterValues.user_name || ''}
                          onChange={(e) => handleFilterChange('user_name', e.target.value)}
                          placeholder="Name"
                        />
                      </th>
                      <th />
                      <th>
                        <input
                          type="text"
                          className={styles.inputSearch2}
                          value={filterValues.actor_name || ''}
                          onChange={(e) => handleFilterChange('actor_name', e.target.value)}
                          placeholder="Name"
                        />
                      </th>
                      <th>
                        <MinimalDateRangePicker
                          handleDateRangeFilterChange={handleDateRangeFilterChange}
                          filterKey="audit_trail"
                          selectedRange={selectedRangeAuditTrailDate}
                          width="100%"
                        />
                      </th>
                    </tr>
                  )}
                </thead>
                {auditTrailList.length > 0 ? (
                  <tbody>
                    {sortedData().map((auditTrailRecord) => {
                      return (
                        <Fragment key={auditTrailRecord.id}>
                          <tr className={`${styles.providerListTableBody}`}>
                            <td className={`${styles.mobileDue}`}>
                              <div className={`${styles.due} fs-exclude`}>
                                <span className={styles.mobileHeader}>Name</span>
                                <span className={styles.mobileDueDate}>
                                  {auditTrailRecord?.user
                                    ? `
                                    ${auditTrailRecord.user?.full_name} (${auditTrailRecord.user?.role?.name})`
                                    : 'N/A'}
                                </span>
                              </div>
                            </td>
                            <td className={`${styles.mobileDue} `}>
                              <div className={`${styles.due} fs-exclude`}>
                                <span className={styles.mobileHeader}>Action</span>
                                <span className={styles.mobileDueDate}>{auditTrailRecord.action_message}</span>
                              </div>
                            </td>
                            <td className={`${styles.mobileDue} `}>
                              <div className={`${styles.due} fs-exclude`}>
                                <span className={styles.mobileHeader}>Modified by</span>
                                <span className={styles.mobileDueDate}>
                                  {auditTrailRecord.actor?.full_name} ({auditTrailRecord.actor?.role?.name})
                                </span>
                              </div>
                            </td>
                            <td className={`${styles.mobileDue}`}>
                              <div className={`${styles.due} fs-unmask`}>
                                <span className={styles.mobileHeader}>Modification date</span>
                                <span className={styles.mobileDueDate}>{formatDate(auditTrailRecord.created_at)}</span>
                              </div>
                            </td>
                          </tr>
                          <tr className={styles.noStartTime} key={`${auditTrailRecord.id}-3`}>
                            <div>
                              <span />
                            </div>
                          </tr>
                        </Fragment>
                      );
                    })}
                    <tr className={styles.lastItemRef} ref={lastAuditTrailItemRef} />
                  </tbody>
                ) : (
                  <span className={styles.noData}>No data to display</span>
                )}
              </table>
              {isLoading && <Loading dotted />}
            </div>
          </>
        ) : (
          <span>
            <Loading dotted />
          </span>
        )}
      </div>
    </div>
  );
};
export default AuditTrailList;
